"use client";

import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics";
import { useProductHub } from "./ProductHubContext";

/** Eigene Domains zaehlen nie als extern — auch nicht von Staging nach Production. */
const OWN_DOMAINS = ["dialetics.de", "dialetics.com"];

/** Selektor des Hub-Inhalts; Klicks in Header, Footer und Navigation zaehlen nicht. */
const PRODUCT_HUB_ROOT_SELECTOR = "[data-product-hub]";

const TRACK_URL = "/api/product-hubs/track";

function isExternal(url: URL): boolean {
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  if (url.host === window.location.host) return false;
  return !OWN_DOMAINS.some((d) => url.hostname === d || url.hostname.endsWith(`.${d}`));
}

/**
 * Zaehlung ohne Cookies. sendBeacon ueberlebt das Verlassen der Seite; als
 * String gesendet (text/plain), weil ein JSON-Blob in manchen Browsern
 * wegen CORS abgelehnt wird. Klappt der Beacon nicht, faellt es auf fetch
 * zurueck — jeweils einzeln abgesichert, damit ein Fehler im einen den
 * anderen nicht verhindert.
 */
function sendToStats(payload: Record<string, string>) {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon?.(TRACK_URL, body)) return;
  } catch {
    // weiter mit fetch
  }
  try {
    fetch(TRACK_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Zaehlen darf die Seite nie stoeren.
  }
}

/**
 * Misst pro Produkt-Hub zweierlei, jeweils doppelt:
 * - PostHog (ueber trackEvent): `product_hub_viewed`, `product_hub_outbound_clicked`
 * - cookielos (/api/product-hubs/track): Grundlage der Admin-Reports
 *
 * Extern ist jeder http(s)-Link auf eine fremde Domain — egal aus welchem
 * Block. Gezaehlt wird beim Klick, auch Mittelklick (neuer Tab). Nicht
 * erfasst: "In neuem Tab oeffnen" ueber das Kontextmenue, und Links in
 * Overlays, die ausserhalb des Hub-Inhalts gerendert werden (Portals).
 *
 * Der Aufruf haengt nur am Slug: Name und Unternehmen kommen aus einer Ref,
 * damit Tippen im Storyblok-Editor keine weiteren Aufrufe ausloest.
 */
const ProductHubTracker = () => {
  const hub = useProductHub();
  const hubRef = useRef(hub);
  hubRef.current = hub;
  const slug = hub?.slug;

  useEffect(() => {
    const current = hubRef.current;
    if (!slug || !current) return;

    trackEvent("product_hub_viewed", {
      hub_slug: slug,
      hub_name: current.name,
      company: current.company,
    });
    sendToStats({ type: "view", hub_slug: slug });
  }, [slug]);

  useEffect(() => {
    if (!slug) return;

    const onClick = (event: MouseEvent) => {
      // Linksklick (click) oder Mittelklick (auxclick, Taste 1)
      if (event.type === "auxclick" && event.button !== 1) return;

      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || !anchor.closest(PRODUCT_HUB_ROOT_SELECTOR)) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (!isExternal(url)) return;

      const current = hubRef.current;
      const domain = url.hostname.replace(/^www\./, "");
      const label = (anchor.innerText || anchor.getAttribute("aria-label") || "").trim().slice(0, 200);

      trackEvent("product_hub_outbound_clicked", {
        hub_slug: slug,
        hub_name: current?.name,
        company: current?.company,
        url: url.href,
        domain,
        label,
      });
      sendToStats({ type: "outbound", hub_slug: slug, domain });
    };

    // Capture-Phase: zaehlt auch, wenn ein Block das Event selbst abfaengt.
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, [slug]);

  return null;
};

export { ProductHubTracker };
